import {
  commitFieldValue,
  FieldWrapper,
  getFieldErrorMessage,
} from "@birdwatch/ui/form";
import type { ReactNode } from "react";
import { type FieldBindingProps, PolyField, pickBinding } from "./poly-field";

/** The slice of a control's props the factory drives itself. */
export type PickerControlProps<TValue> = {
  value: TValue;
  onChange: (value: TValue) => void;
  disabled?: boolean;
  className?: string;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
  /** Shows an X in the trigger that sets the value back to empty. */
  clearable?: boolean;
};

/**
 * The control a picker field wraps.
 *
 * The `Record<string, any>` member is what lets a control take extras
 * (`placeId`, `timeZone`, `assigneeId`, …) that `config.controlProps` supplies.
 * The signature sits in METHOD position on purpose: TypeScript checks method
 * parameters bivariantly, which is what lets a control declare those extras as
 * REQUIRED — an index signature does not satisfy a required property, so a
 * plain `ComponentType<…>` would reject a control with required extras
 * (`placeId`, `timeZone`) outright.
 * `value` / `onChange` are still checked against `TValue` in both directions,
 * so a value-shape mismatch remains a compile error.
 */
export type PickerControl<TValue> = {
  control(props: PickerControlProps<TValue> & Record<string, any>): ReactNode;
}["control"];

/** Props EVERY generated picker field accepts, on top of `name | field`. */
export type PickerFieldOwnProps<TValue> = {
  label?: ReactNode;
  description?: ReactNode;
  /** Renders the "(Optional)" affix via FieldWrapper. */
  optional?: boolean;
  /** An action at the right end of the label row. Lands on FieldWrapper. */
  labelAction?: ReactNode;
  /**
   * An X in the trigger clears the value. Forwarded only when set, so a control that can't clear
   * never receives it.
   */
  clearable?: boolean;
  disabled?: boolean;
  /** Layout of the whole field block. Lands on the FieldWrapper root. */
  className?: string;
  /** Sizing/border/typography of the trigger itself. Lands on the CONTROL. */
  inputClassName?: string;
  /** Inline-edit pop-out controls. Forwarded only when `config.popout` is set. */
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
  /** Post-commit hook — fires AFTER the value is committed. For sibling-field side effects. */
  onValueChange?: (value: TValue) => void;
  /** Rendered after the FieldWrapper inside the same field slot (banners). */
  footer?: ReactNode;
  /** Consumer-owned message that overrides the field's own error. */
  error?: string;
};

export type PickerFieldProps<
  TValue,
  TExtra = unknown,
> = PickerFieldOwnProps<TValue> & TExtra & FieldBindingProps;

export type MakePickerFieldConfig<TValue, TExtra> = {
  /** React displayName. Must equal the exported component name. */
  name: string;
  control: PickerControl<TValue>;
  /** Default `label` used when the caller passes none. */
  label?: ReactNode;
  /** Forward `defaultOpen` / `onOpenChange` / `modal` to the control. */
  popout?: boolean;
  /**
   * Per-field extras for the control (`placeId`, `params`, `timeZone`,
   * `placeholder`, `align`, …), derived from the caller's props.
   * Runs on every render — must be a pure CALCULATION: no hooks, no Date.now(),
   * no reads of module-level mutable state.
   */
  controlProps?: (props: TExtra) => Record<string, unknown>;
};

export function makePickerField<TValue, TExtra = unknown>(
  config: MakePickerFieldConfig<TValue, TExtra>,
) {
  const Control = config.control;

  function PickerField(props: PickerFieldProps<TValue, TExtra>) {
    const {
      label = config.label,
      description,
      optional,
      labelAction,
      disabled,
      className,
      inputClassName,
      defaultOpen,
      onOpenChange,
      modal,
      onValueChange,
      footer,
      error,
      clearable,
    } = props;
    const binding = pickBinding(props);
    const popout = config.popout ? { defaultOpen, onOpenChange, modal } : {};
    const extras = config.controlProps?.(props as unknown as TExtra) ?? {};

    return (
      <PolyField<TValue> {...binding}>
        {(field) => {
          return (
            <>
              <FieldWrapper
                label={label}
                description={description}
                optional={optional}
                labelAction={labelAction}
                error={error ?? getFieldErrorMessage(field)}
                disabled={disabled}
                className={className}
              >
                <Control
                  value={field.state.value}
                  onChange={(value: TValue) => {
                    // A picker selection IS the commit — see the round-2
                    // validation policy: discrete inputs never fire a DOM blur,
                    // so `commitFieldValue` marks the field blurred and its error
                    // becomes visible immediately.
                    commitFieldValue(field, value);
                    onValueChange?.(value);
                  }}
                  disabled={disabled}
                  className={inputClassName}
                  {...popout}
                  {...(clearable === undefined ? {} : { clearable })}
                  {...extras}
                />
              </FieldWrapper>
              {footer}
            </>
          );
        }}
      </PolyField>
    );
  }

  PickerField.displayName = config.name;
  return PickerField;
}
